import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useRequestData, usePrefs, useLabels } from 'wsm-common-data';
import { formatNumber } from 'ddc-intl';
import { toggleMake } from '../../features/make/makeSlice';

/**
 * Renders a list of OEM/make checkboxes with counts.
 */
const MakeFacetGroup = ({ makes, selectedMakes }) => {
	const dispatch = useDispatch();
	const { locale } = useRequestData();
	const { showFacetCounts, showCountParenthesis, makeFacetLimit } =
		usePrefs();
	const labels = useLabels();
	const [isExpanded, setIsExpanded] = useState(false);

	const limit = parseInt(makeFacetLimit, 10) || 7;
	const shouldTruncate = makes.length > limit;
	const visibleMakes =
		shouldTruncate && !isExpanded ? makes.slice(0, limit) : makes;

	if (!makes || makes.length === 0) {
		return null;
	}

	const handleToggle = (value) => {
		dispatch(toggleMake(value));
	};

	const renderCount = (count) => {
		if (showFacetCounts !== 'true' || count <= 1) return null;
		const formattedCount = formatNumber(count, locale);
		const countValue =
			showCountParenthesis === 'true'
				? `(${formattedCount})`
				: formattedCount;
		return (
			<small className="ddc-font-size-xsmall">&#xFEFF;{countValue}</small>
		);
	};

	return (
		<div className="make-facet-group">
			<ul className="list-unstyled mb-2">
				{visibleMakes.map(({ value, label, count }) => {
					const checkboxId = `make-facet-${value}`;
					const isChecked = selectedMakes.includes(value);
					return (
						<li key={value} className="checkbox mb-lg-4">
							<label htmlFor={checkboxId}>
								<input
									type="checkbox"
									className="mt-3"
									name="make"
									value={value}
									id={checkboxId}
									checked={isChecked}
									onChange={() => handleToggle(value)}
								/>
								<span className="facet-list-facet-label-text-container">
									<span
										className={`facet-list-facet-label-text${
											!isChecked ? ' text-muted' : ''
										}`}
									>
										{label}
										{renderCount(count)}
									</span>
								</span>
							</label>
						</li>
					);
				})}
			</ul>
			{shouldTruncate && (
				<button
					type="button"
					className="text-link"
					onClick={() => setIsExpanded((prev) => !prev)}
				>
					{isExpanded
						? labels.get('SHOW_FEWER_MAKES')
						: labels.get('SHOW_ALL_MAKES')}
				</button>
			)}
		</div>
	);
};

MakeFacetGroup.propTypes = {
	makes: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			count: PropTypes.number.isRequired
		})
	).isRequired,
	selectedMakes: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default MakeFacetGroup;
